import { NominationScope } from '../nomination.types';

interface FirebaseLikeError {
  code?: unknown;
  message?: unknown;
}

const describeScope = (scope?: NominationScope | null): string => {
  if (!scope) return '';
  const location = scope.assignedLocations?.length ? ` at "${scope.assignedLocations.join('", "')}"` : '';
  return ` The app queried as ${scope.role}${location}.`;
};

/**
 * Turns a Firestore error into guidance an operator can act on. Firestore's own
 * messages ("Missing or insufficient permissions.") do not say whether rules are
 * undeployed, the account lacks the role, or an index is still building.
 */
export function describeNominationError(error: unknown, scope?: NominationScope | null): string {
  const raw = (error ?? {}) as FirebaseLikeError;
  const code = typeof raw.code === 'string' ? raw.code : '';
  const message = typeof raw.message === 'string' ? raw.message : '';

  if (code.includes('permission-denied')) {
    return (
      'Firestore denied access to nominations.' +
      describeScope(scope) +
      ' Check that the latest security rules are published (firebase deploy --only firestore:rules) and that this ' +
      "account's Firestore role is MANAGER or ADMIN. The dev role override only changes the UI, not what the rules see."
    );
  }

  if (code.includes('failed-precondition')) {
    return (
      'This view needs a Firestore composite index that has not been built yet. Deploy the indexes ' +
      '(firebase deploy --only firestore:indexes) and wait for them to finish building. The browser console ' +
      'contains the direct link to create it.'
    );
  }

  if (code.includes('unavailable')) {
    return 'Firestore is unreachable right now. Check your connection and retry.';
  }

  return message || 'Failed to load nominations.';
}
